import { useState, useEffect } from 'react';
import { Card, Button, Input, Badge } from '../components/ui';
import { clsx } from 'clsx';
import { Plus, Edit2, Check, X, Trash2 } from 'lucide-react';
import {
  reviewTemplateApi,
  ReviewTemplate,
  CreateReviewTemplateInput,
  Sentiment,
} from '../lib/api';

const SENTIMENT_LABELS: Record<Sentiment, string> = {
  POSITIVE: 'Положительный',
  NEGATIVE: 'Отрицательный',
  NEUTRAL: 'Нейтральный',
};

const SENTIMENT_TONES: Record<Sentiment, 'success' | 'danger' | 'neutral'> = {
  POSITIVE: 'success',
  NEGATIVE: 'danger',
  NEUTRAL: 'neutral',
};

const SENTIMENTS = Object.keys(SENTIMENT_LABELS) as Sentiment[];

const emptyForm: CreateReviewTemplateInput = {
  name: '',
  category: 'POSITIVE',
  content: '',
};

export function TemplatesSection() {
  const [templates, setTemplates] = useState<ReviewTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState<CreateReviewTemplateInput>(emptyForm);
  const [formError, setFormError] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<CreateReviewTemplateInput>>({});

  useEffect(() => {
    setLoading(true);
    reviewTemplateApi
      .getAll()
      .then((res) => {
        setTemplates(res.data.data);
        setError('');
      })
      .catch(() => setError('Не удалось загрузить шаблоны'))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.content.trim()) {
      setFormError('Заполните все поля');
      return;
    }
    try {
      const res = await reviewTemplateApi.create(form);
      setTemplates((prev) => [...prev, res.data.data]);
      setForm(emptyForm);
      setShowAddForm(false);
      setFormError('');
    } catch {
      setFormError('Не удалось создать шаблон');
    }
  };

  const handleEditSave = async (id: string) => {
    try {
      const res = await reviewTemplateApi.update(id, editForm);
      setTemplates((prev) => prev.map((t) => (t.id === id ? res.data.data : t)));
      setEditingId(null);
      setEditForm({});
    } catch {
      setError('Не удалось сохранить шаблон');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить шаблон?')) return;
    try {
      await reviewTemplateApi.remove(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch {
      setError('Не удалось удалить шаблон');
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-400">Загрузка…</p>;
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {!showAddForm && (
        <Button
          variant="outline"
          size="sm"
          icon={<Plus className="w-3.5 h-3.5" />}
          onClick={() => setShowAddForm(true)}
        >
          Добавить шаблон
        </Button>
      )}

      {/* Add form */}
      {showAddForm && (
        <Card className="bg-gray-50">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Новый шаблон</h4>
          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-3">
              {formError}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Input
              label="Название *"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Краткое название шаблона"
            />
            <div>
              <label className="block text-xs text-gray-500 mb-1">Тип отзыва</label>
              <select
                value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value as Sentiment }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
              >
                {SENTIMENTS.map((s) => (
                  <option key={s} value={s}>{SENTIMENT_LABELS[s]}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-xs text-gray-500 mb-1">Текст шаблона *</label>
            <textarea
              value={form.content}
              onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
              rows={4}
              placeholder="Текст ответа, который будет использоваться как шаблон…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate}>Сохранить</Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddForm(false);
                setForm(emptyForm);
                setFormError('');
              }}
            >
              Отмена
            </Button>
          </div>
        </Card>
      )}

      {/* List by sentiment */}
      {templates.length === 0 && !showAddForm ? (
        <p className="text-sm text-gray-400">Нет шаблонов</p>
      ) : (
        SENTIMENTS.map((sentiment) => {
          const group = templates.filter((t) => t.category === sentiment);
          if (group.length === 0) return null;
          return (
            <div key={sentiment}>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {SENTIMENT_LABELS[sentiment]} ({group.length})
              </h4>
              <div className="space-y-2 mb-3">
                {group.map((tmpl) => (
                  <div
                    key={tmpl.id}
                    className={clsx('border rounded-xl p-3 shadow-card border-gray-200 bg-white')}
                  >
                    {editingId === tmpl.id ? (
                      <div className="space-y-2">
                        <Input
                          value={editForm.name ?? tmpl.name}
                          onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                        />
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Тип отзыва</label>
                          <select
                            value={editForm.category ?? tmpl.category}
                            onChange={(e) =>
                              setEditForm((p) => ({ ...p, category: e.target.value as Sentiment }))
                            }
                            className="w-full border border-primary-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
                          >
                            {SENTIMENTS.map((s) => (
                              <option key={s} value={s}>{SENTIMENT_LABELS[s]}</option>
                            ))}
                          </select>
                        </div>
                        <textarea
                          value={editForm.content ?? tmpl.content}
                          onChange={(e) => setEditForm((p) => ({ ...p, content: e.target.value }))}
                          rows={4}
                          className="w-full border border-primary-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Check className="w-4 h-4 text-green-600" />}
                            onClick={() => handleEditSave(tmpl.id)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<X className="w-4 h-4 text-gray-400" />}
                            onClick={() => { setEditingId(null); setEditForm({}); }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900">{tmpl.name}</p>
                            <Badge tone={SENTIMENT_TONES[tmpl.category]}>
                              {SENTIMENT_LABELS[tmpl.category]}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{tmpl.content}</p>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEditingId(tmpl.id); setEditForm({}); }}
                            icon={<Edit2 className="w-3.5 h-3.5" />}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(tmpl.id)}
                            icon={<Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
